/* eslint-disable import/order */
/* eslint-disable no-console */
import { useState, useEffect, useCallback } from 'react';
import {
  NavigateFunction,
  Outlet,
  Path,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import ROUTES from './routes';

import { Footer, FooterComplex, Splash } from '@/components';
import useAuth from '@/hooks/useAuth';
import useCountMedals from '@/hooks/useCountMedals';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import {
  refreshAccountInitialData,
  ResponseGetAccountInitialData,
  setShouldCheckLoggedInNative,
} from '@/services';
import { handleFaqUrl, handleInvitationUrl, TEXTS } from '@/utils';

import { getMedals } from '@/services/countMedals';
import { getListTDA } from '@/services/tda';
import { PinsAccountData, CountMedalsData } from '@/store';
import { invitacionesTkred } from '@/services/invites';
import { InvitationType, ResponseAccionPostLogin } from '@/types/invites';
import { toast } from 'react-toastify';
import { tracking } from '@/utils/lib';
import {
  initialTshitAndShield,
  TshitAndShieldTypes,
} from '@/store/tshirtAndShield';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import { SvgCamiseta, SvgEscudo } from '@/types/editProfile';
import {
  parseParamsTshirtAndShield,
  shieldKeys,
  tshirtKeys,
} from '@/modules/edit-my-account/functions/utils';

/**
 * AuthenticatedNativo
 * <br>
 * Explicado en useAuth.
 * <quote> prelogueado (android + OTP anterior)" (..)</quote>
 * @see {useAuth}
 */

const AuthenticatedNativo = () => {
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { show } = useToast();
  const { handleSetTournamentMaps } = useTournament();
  const { handleSetPinsData, pinsData } = usePinsAccount();
  const { handleSetMedalsData } = useCountMedals();

  const {
    isAuthenticated,
    logout,
    setUserInitialData,
    isLoading: authHookLoading,
  } = useAuth();

  const { setTshirtAndShield } = useTshirtAndShield();
  const location = useLocation();

  const handleRedirectAfterLogin = useCallback(async () => {
    setUserInitialData(null);
    if (authHookLoading) {
      return;
    }
    if (!isAuthenticated) {
      setUserInitialData(null);
      // show(TEXTS.defaultError);
      // logout();
      setLoading(false);
      return;
    }
    try {
      const result = await refreshAccountInitialData();

      if (result && result.estado) {
        await processInitialDataEstadoOk(
          result,
          setUserInitialData,
          setTshirtAndShield,
          handleSetTournamentMaps,
          handleSetPinsData,
          handleSetMedalsData,
          navigate,
          location
        );
        setLoading(false);
      } else {
        setUserInitialData(null);
        console.log('2.2');
        show(TEXTS.defaultError);
        logout();
        setLoading(false);
      }
    } catch (error) {
      const err = error as Error;
      show(err.message);
      setShouldCheckLoggedInNative(false);
      navigate(ROUTES.notFound, {
        replace: true,
      });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHookLoading, isAuthenticated]);

  useEffect(() => {
    handleRedirectAfterLogin();
  }, [handleRedirectAfterLogin]);

  if (isLoading) {
    return <Splash />;
  }

  // oculto el footer para urls determinadas
  const showMinimalFooter =
    shouldShowMinimalFooter(window.location.pathname) ||
    (!isAuthenticated &&
      window.location.pathname === ROUTES.preguntasFrecuentes);

  // oculto el footer para urls determinadas
  const hideFooter = shouldHideFooter(window.location.pathname);

  return (
    <>
      <Outlet />
      {!hideFooter &&
        (showMinimalFooter ? (
          <Footer />
        ) : (
          <FooterComplex isPremium={pinsData.isPremium} />
        ))}
    </>
  );
};

export function shouldShowMinimalFooter(pathname: string) {
  const paths = [
    ROUTES.basePath,
    ROUTES.notFound,
    ROUTES.recoverEmail,
    ROUTES.registration,
    ROUTES.registrationSchool,
  ];
  return paths.find((path) => path === pathname);
}

export function shouldHideFooter(pathname: string) {
  const paths = [
    ROUTES.welcome,
    ROUTES.welcomeF5,
    ROUTES.teamConfirm,
    ROUTES.teamConfirmF5,
    ROUTES.registrationNameTeam,
  ];
  return paths.find((path) => path === pathname);
}

export const resolveInvitationRedirect = (
  pathname: string
): { href?: string; error?: boolean } => {
  const blockedRoutes = [
    ROUTES.preguntasFrecuentes,
    ROUTES.friendsTournament,
    ROUTES.friendsCup.home,
    ROUTES.minileagues.home,
  ];

  if (blockedRoutes.includes(pathname)) {
    return {};
  }

  const faqResult = handleFaqUrl(pathname);
  if (faqResult) {
    return { href: ROUTES.preguntasFrecuentes };
  }
  const invitationResult = handleInvitationUrl(pathname);

  if (!invitationResult) {
    return { error: true };
  }

  const mapRoute = (type?: string) => {
    switch (type) {
      case InvitationType.COPA:
        return ROUTES.friendsCup.home;
      case InvitationType.TORNEO:
        return ROUTES.friendsTournament;
      case InvitationType.MINILIGA:
        return ROUTES.minileagues.home;
      default:
        return undefined;
    }
  };

  // Caso directo
  const directRoute = mapRoute(String(invitationResult));
  if (directRoute) return { href: directRoute };

  // Caso con localStorage
  const invitationId = localStorage.getItem('invitationId');
  const invitationType = localStorage.getItem('invitationType');

  if (invitationId) {
    const route = mapRoute(invitationType || undefined);
    if (route) return { href: route };
  }

  return { error: true };
};

export async function processInitialDataEstadoOk(
  result: ResponseGetAccountInitialData,
  setUserInitialData: (data: ResponseGetAccountInitialData | null) => void,
  setTshirtAndShield: (data: TshitAndShieldTypes) => void,
  handleSetTournamentMaps: (data: {
    tournamentStatus: ResponseGetAccountInitialData['status_torneo'];
    tournamentTeams: ResponseGetAccountInitialData['equipos_torneo'];
  }) => void,
  handleSetPinsData: (data: PinsAccountData) => void,
  handleSetMedalsData: (data: CountMedalsData) => void,
  navigate: NavigateFunction,
  location: Path
) {
  const { trackRegistration } = tracking();
  trackRegistration({
    modo: result.modoRegistracion,
    paseId: result.pase_id,
    edadDt: result?.edad_dt,
    // dataSource: !isDesktop() ? 'app' : 'web',
    cdSexo:
      Number(result.idSexo) === 1
        ? 'm'
        : Number(result.idSexo) === 2
        ? 'f'
        : 'otro',
    usuario: result.premium ? 'Premium' : 'Normal',
  });
  const tournamentStatus = result.status_torneo;
  const tournamentTeams = result.equipos_torneo;

  setUserInitialData(result);
  handleSetTournamentMaps({ tournamentStatus, tournamentTeams });
  handleSetPinsData({
    showInputsModalPins: false,
    showModalWhatsPins: false,
    accountMoney: result.saldo,
    isPremium: result.premium,
    pinsAvailable: result.pinesDisponibles,
    showModalWallet: false,
  });

  // Generamos los datos dinámicamente
  const tshirtData = parseParamsTshirtAndShield<SvgCamiseta>(
    result?.camiseta_svg,
    tshirtKeys,
    initialTshitAndShield.tshirt
  );

  const shieldData = parseParamsTshirtAndShield<SvgEscudo>(
    result?.escudo_svg,
    shieldKeys,
    initialTshitAndShield.shield
  );

  setTshirtAndShield({
    tshirt: tshirtData,
    shield: shieldData,
  });

  // llamo api medallas y seteo las medallas del usuario en global una vez
  const countMedals = await getMedals();
  countMedals.estado && handleSetMedalsData(countMedals);

  const { href } = resolveInvitationRedirect(location.pathname);

  if (!result.tiene_equipo) {
    if (location.pathname === ROUTES.preguntasFrecuentes) {
      navigate(ROUTES.preguntasFrecuentes, {
        replace: true,
      });
    } else {
      navigate(ROUTES.welcome, {
        replace: true,
      });
    }
  } else {
    const getList = await getListTDA();
    getList.estado &&
      setUserInitialData({
        ...result,
        showTDAP:
          getList.abiertos.length > 0 || getList.participaciones.length > 0,
      });

    if (href) {
      navigate(href, {
        replace: true,
      });
      return;
    }

    // check if loguin is from tkred invitation
    const parsed = sessionStorage.getItem('tkRed') || undefined;
    if (parsed) {
      await invitacionesTkredManager(parsed, navigate);
      return;
    }

    // Check if the current location is the private FAQ page before navigating to summary
    if (window.location.pathname !== ROUTES.preguntasFrecuentes) {
      if (result?.paginaInicial?.linkWebResponsive) {
        const pagInicial = result?.paginaInicial
          ?.linkWebResponsive as keyof typeof ROUTES;
        if (pagInicial.startsWith(ROUTES.friendsTournamentPrizes)) {
          if (
            getList.estado &&
            (getList.abiertos.length > 0 || getList.participaciones.length > 0)
          ) {
            navigate(pagInicial, {
              replace: true,
            });
          } else {
            navigate(ROUTES.friendsTournament, {
              replace: true,
            });
          }
        } else {
          navigate(pagInicial, {
            replace: true,
          });
        }
      } else {
        navigate(ROUTES.summary, {
          replace: true,
        });
      }
    }
  }
}

const invitacionesTkredManager = async (
  parsed: any,
  navigate: NavigateFunction
) => {
  const parse = JSON.parse(parsed);
  try {
    const resp = await invitacionesTkred(parse);
    if (resp.estado) {
      if (!resp.postLogin.noEsOpUs) {
        sessionStorage.setItem(
          'postLoginTkred',
          JSON.stringify({ isOpenModal: true, data: resp })
        );
        redirectFromTkredResponse(resp, navigate);
      } else {
        toast(
          'La invitación no corresponde al documento con el que ingresaste al sitio',
          {
            containerId: 'global',
            role: 'alert',
            position:
              window.innerWidth > 1024
                ? toast.POSITION.TOP_CENTER
                : toast.POSITION.BOTTOM_LEFT,
            className: 'font-sans font-medium',
          }
        );
        navigateAndRemoveStorage(navigate, ROUTES.summary);
      }
    }
  } catch (error) {
    sessionStorage.removeItem('tkRed');
    navigateAndRemoveStorage(navigate, ROUTES.summary);
  }
};

const redirectFromTkredResponse = (
  resp: ResponseAccionPostLogin,
  navigate: NavigateFunction
) => {
  if (resp.postLogin.opInfo.invitacionTdA) {
    navigateAndRemoveStorage(navigate, `${ROUTES.friendsTournament}`);
  } else if (resp.postLogin.opInfo.invitacionGCA) {
    navigateAndRemoveStorage(navigate, `${ROUTES.friendsCup.home}`);
  } else if (resp.postLogin.opInfo.invitacionML) {
    navigateAndRemoveStorage(navigate, `${ROUTES.minileagues.home}`);
  } else {
    navigateAndRemoveStorage(navigate, ROUTES.summary);
  }
};

const navigateAndRemoveStorage = (
  navigate: NavigateFunction,
  route: string
) => {
  navigate(route, {
    replace: true,
  });
  sessionStorage.removeItem('tkRed');
};

export default AuthenticatedNativo;
